import { CORE_PAGES } from '@core-ui/components';
import { SubscriptionStatus } from '@moniebook-web/types';
import dayjs from 'dayjs';

import {
    type BannerInfo,
    BannerType,
    type SubscriptionStatusDetails,
} from './types';

export const SUBSCRIPTION_GRACE_PERIOD_IN_DAYS = 5;
export const REMAINING_DAYS_TO_SHOW_FREE_TRIAL_BANNER = 5;

export const isSubscriptionInGracePeriod = (endDate: string) => {
    const today = dayjs().startOf('day');
    const gracePeriodStart = dayjs(endDate).add(1, 'day');
    const gracePeriodEnd = gracePeriodStart.add(5, 'day');

    return (
        today.diff(gracePeriodStart, 'day') >= 0 &&
        today.isBefore(gracePeriodEnd)
    );
};

export const getBannerContent = (
    subscriptionStatusData?: SubscriptionStatusDetails,
    announcementBanner?: BannerInfo,
): BannerInfo | undefined => {
    const link = '/dashboard/settings/subscriptions';

    if (!subscriptionStatusData) {
        return {
            id: 'subscriptions-info',
            completed: false,
            type: BannerType.INFO,
            message:
                "Looks like you've not subscribed yet. Subscribe now to keep things running",
            label: 'Subscribe now!',
            link,
        };
    }

    const {
        status,
        subscriptionEndDate,
        active: subscriptionIsActive,
    } = subscriptionStatusData;

    if (!subscriptionEndDate) {
        return announcementBanner;
    }

    if (
        status === SubscriptionStatus.ACTIVE &&
        isSubscriptionInGracePeriod(subscriptionEndDate)
    ) {
        const gracePeriodStart = dayjs(subscriptionEndDate).add(1, 'day');
        const gracePeriodElapsed = dayjs().diff(gracePeriodStart, 'days');
        const remainingDays =
            SUBSCRIPTION_GRACE_PERIOD_IN_DAYS - gracePeriodElapsed;

        return {
            id: 'subscription-grace-period',
            completed: false,
            type: BannerType.INFO,
            message: `Your subscription renewal failed! You're in a ${remainingDays}-day grace period. We'll retry soon, ensure your account is funded.`,
            label: 'View subscription',
            link,
        };
    }

    if (
        status === SubscriptionStatus.FREE_TRIAL &&
        isSubscriptionInGracePeriod(subscriptionEndDate)
    ) {
        return {
            id: 'subscription-grace-period',
            completed: false,
            type: BannerType.INFO,
            message:
                "Your subscription renewal is due. You're in a 5-day grace period. We'll try to charge you tonight, please ensure your Moniepoint account is funded.",
            label: 'Pay now',
            link: CORE_PAGES.SETTINGS.CONFIRM_PAYMENT,
        };
    }

    const remainingDays = dayjs(subscriptionEndDate)
        .add(1, 'day') // Add one day to account for today
        .diff(dayjs(), 'days');
    const isBeyondEndDate = remainingDays < 0;

    if (status === SubscriptionStatus.CANCELLED && isBeyondEndDate) {
        return {
            id: 'subscription-cancelled-no-active',
            completed: false,
            type: BannerType.ERROR,
            message:
                'You do not have an active subscription, Reactivate to continue to have full access to this page.',
            label: 'Reactivate Now',
            link: CORE_PAGES.SETTINGS.CONFIRM_PAYMENT,
        };
    }

    if (status === SubscriptionStatus.CANCELLED) {
        return {
            id: 'subscription-cancelled-active',
            completed: false,
            type: BannerType.WARN,
            message: `Your cancellation date is in ${remainingDays} days, reactivate now to continue to have access to our offerings.`,
            label: 'Reactivate Now',
            link: CORE_PAGES.SETTINGS.SUBSCRIPTIONS,
            endDate: subscriptionEndDate,
        };
    }

    if (status === SubscriptionStatus.EXPIRED) {
        return {
            id: 'subscription-expired',
            completed: false,
            type: BannerType.ERROR,
            message:
                'Your subscription has expired, Reactivate now to continue to have full access to this page.',
            label: 'Reactivate Now',
            link: CORE_PAGES.SETTINGS.CONFIRM_PAYMENT,
        };
    }

    if (isBeyondEndDate) {
        return announcementBanner;
    }

    if (
        status === SubscriptionStatus.FREE_TRIAL &&
        subscriptionIsActive &&
        remainingDays <= REMAINING_DAYS_TO_SHOW_FREE_TRIAL_BANNER
    ) {
        const endMessage =
            remainingDays === 0 ? 'today' : `in ${remainingDays} days`;

        return {
            id: 'subscription-free-trial',
            completed: false,
            type: BannerType.INFO,
            message: `Your free trial ends ${endMessage}! Your subscription will be renewed, and your account will be charged.`,
            label: 'Pay now',
            link: CORE_PAGES.SETTINGS.CONFIRM_PAYMENT,
        };
    }

    return announcementBanner;
};
